import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToasterService } from '../../../service/common/toaster.service';
import { CommonService, CrudComponent } from 'wire-frame';
import { PurchaseOrderComponent } from '../purchaseorder/purchaseorder.component';
import { ClinicSupplyComponent } from '../clinicsupply/clinicsupply.component';

// PrimeNG
import { TagModule } from 'primeng/tag';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DropdownModule } from 'primeng/dropdown';
import { ToolbarModule } from 'primeng/toolbar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import { TabViewModule } from 'primeng/tabview';
import { AccordionModule } from 'primeng/accordion';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DialogModule } from 'primeng/dialog';

// Service
import { AdminInventoryService } from '../../../service/admin/admin-inventory.service';

// ====== Types ======
export interface Item {
  itemName: string;
  quantity: number;
  status: 'Available' | 'Low Stock' | 'Out of Stock';
}

export interface AdminInventoryRecord {
  id: string;
  itemName?: string;
  quantity?: number;
  status?: 'Available' | 'Low Stock' | 'Out of Stock';
}

export type Inventory = Record<string, Item[]>;

// ====== Component ======
@Component({
  selector: 'app-admin-inventory',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    TagModule,
    ToggleButtonModule,
    DropdownModule,
    ToolbarModule,
    ConfirmDialogModule,
    CrudComponent,
    EditorModule,
    ButtonModule,
    TabViewModule,
    AccordionModule,
    ScrollPanelModule,
    DialogModule,
    PurchaseOrderComponent,
    ClinicSupplyComponent,
  ],
  providers: [ConfirmationService, CommonService, AdminInventoryService],
  templateUrl: './admin-inventory.component.html',
  styleUrls: ['./admin-inventory.component.scss'],
})
export class AdminInventoryComponent implements OnInit {
  // ====== UI & CRUD ======
  closeFormEvent = false;
  openDeletePopup = false;

  AdminInventoryList: AdminInventoryRecord[] = [];
  AdminInventory: AdminInventoryRecord = { id: '' };
  editRecord: AdminInventoryRecord | null = null;

  storeInventory: Inventory = {
    Warehouse: [
      { itemName: 'Paracetamol ( 100 ml )', quantity: 100, status: 'Available' },
      { itemName: 'Paracetamol ( 30 ml )', quantity: 60, status: 'Available' },
      { itemName: 'Paracetamol ( 10 ml )', quantity: 45, status: 'Low Stock' },
      { itemName: 'Cough Syrup ( 100 ml )', quantity: 80, status: 'Available' },
      { itemName: 'Cough Syrup ( 30 ml )', quantity: 30, status: 'Low Stock' },
    ],
  };

  // ====== Popups ======
  isPurchaseOrderVisible = false;
  isClinicSupplyVisible = false;
  isHistoryPopupVisible = false;

  selectedItem: Item | null = null;
  selectedClinic: string | null = null;

  constructor(
    public common: CommonService,
    private toast: ToasterService,
    private confirmationService: ConfirmationService,
    private adminInventoryService: AdminInventoryService
  ) {
    // Breadcrumb
    this.common.breadCrum = {
      title: 'Admin Inventory',
      icon: 'users',
      list: ['Admin Inventory'],
    };

    this.common.setCrudTableHeaders([]);
    this.common.setCrudActions([
      { title: '', icon: 'pi-pencil', classArr: 'me-2', tooltip: 'Update' },
      { title: '', icon: 'pi-trash', tooltip: 'Delete' },
    ]);
  }

  ngOnInit(): void {
    this.getAdminInventory();
  }

  // ===== CRUD Methods =====
  getAdminInventory(): void {
    this.adminInventoryService.getAdminInventory().subscribe({
      next: (res: any) => (this.AdminInventoryList = res.data || []),
      error: () =>
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch inventory' }),
    });
  }

  processAdminInventory(): void {
    this.AdminInventory.id ? this.updateAdminInventory() : this.addAdminInventory();
  }

  addAdminInventory(): void {
    this.adminInventoryService.addAdminInventory(this.AdminInventory).subscribe({
      next: (res: any) => this.handleCrudResponse(res, 'Inventory added successfully'),
      error: () =>
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to add inventory' }),
    });
  }

  updateAdminInventory(): void {
    this.adminInventoryService.updateAdminInventory(this.AdminInventory).subscribe({
      next: (res: any) => this.handleCrudResponse(res, 'Inventory updated successfully'),
      error: () =>
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update inventory' }),
    });
  }

  deleteAdminInventory(item: AdminInventoryRecord): void {
    this.adminInventoryService.deleteAdminInventory(item).subscribe({
      next: () => {
        this.toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Inventory item deleted successfully',
        });
        this.getAdminInventory();
      },
      error: () =>
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete inventory' }),
    });
  }

  handleCrudResponse(res: any, successMessage: string): void {
    if (res?.status) {
      this.toast.add({ severity: 'success', summary: 'Success', detail: res.message || successMessage });
      this.getAdminInventory();
      this.clearForm();
    } else {
      this.toast.add({ severity: 'error', summary: 'Error', detail: res?.message || 'Operation failed' });
    }
  }

  clearForm(): void {
    this.AdminInventory = { id: '' };
    this.editRecord = null;
    this.closeFormEvent = true;
    setTimeout(() => (this.closeFormEvent = false), 1);
  }

  // ===== Event Handlers =====
  handleEvent(event: any): void {
    if (event.action.tooltip === 'Update') {
      this.editRecord = event.data;
      this.AdminInventory = { ...event.data };
    } else if (event.action.tooltip === 'Delete') {
      this.openDeletePopup = true;
      this.AdminInventory = { ...event.data };
    }
  }

  deleteRecord(event: any): void {
    if (event) this.deleteAdminInventory(event);
  }

  // ===== Popup Controls =====
  openPurchaseOrder(): void {
    this.isPurchaseOrderVisible = true;
  }
  closePurchaseOrder(): void {
    this.isPurchaseOrderVisible = false;
  }

  openClinicSupply(): void {
    this.isClinicSupplyVisible = true;
  }
  closeClinicSupply(): void {
    this.isClinicSupplyVisible = false;
  }

  openPoHistoryPopup(item: Item, clinic: string): void {
    this.selectedItem = item;
    this.selectedClinic = clinic;
    this.isHistoryPopupVisible = true;
  }
  closeHistoryPopup(): void {
    this.selectedItem = null;
    this.selectedClinic = null;
    this.isHistoryPopupVisible = false;
  }

  // ===== Utility =====
  getSeverity(status: 'Available' | 'Low Stock' | 'Out of Stock'): 'success' | 'warning' | 'danger' {
    switch (status) {
      case 'Available':
        return 'success';
      case 'Low Stock':
        return 'warning';
      case 'Out of Stock':
        return 'danger';
    }
  }

  trackByFn(index: number, item: Item): string {
    return item.itemName; // removed batchNo
  }
}
